(() => {
  class BrandBuildingVideoScroll {
    constructor(section) {
      this.section = section;
      this.track = section.querySelector('[data-bbvs-track]');
      this.stage = section.querySelector('[data-bbvs-stage]');
      this.cluster = section.querySelector('[data-bbvs-cluster]');
      this.intro = section.querySelector('.brand-building-video-scroll__intro');
      this.card = section.querySelector('[data-bbvs-card]');
      this.nextCard = section.querySelector('[data-bbvs-next-card]');
      this.handles = section.querySelector('[data-bbvs-handles]');
      this.owned = section.querySelector('[data-bbvs-owned]');
      this.brand = section.querySelector('[data-bbvs-brand]');
      this.brandNum = section.querySelector('[data-bbvs-num]');
      this.brandName = section.querySelector('[data-bbvs-name]');
      this.brandTick = section.querySelector('[data-bbvs-tick]');
      this.brandGhost = section.querySelector('[data-bbvs-ghost]');
      this.brandDesc = section.querySelector('[data-bbvs-desc]');
      this.brandBtn = section.querySelector('[data-bbvs-btn]');
      this.expand = section.querySelector('.brand-building-video-scroll__expand');
      this.mediaLayers = Array.from(this.card ? this.card.querySelectorAll('[data-bbvs-media-layer]') : []);
      this.nextMediaLayers = Array.from(this.nextCard ? this.nextCard.querySelectorAll('[data-bbvs-next-media-layer]') : []);
      // A slide can carry a desktop video and a mobile video in the same layer,
      // so track all videos per layer and pick the visible one at play time.
      this.layerVideos = this.mediaLayers.map((layer) => Array.from(layer.querySelectorAll('video')));
      this.videos = this.layerVideos.reduce((all, vids) => all.concat(vids), []);
      this.slideData = Array.from(section.querySelectorAll('[data-bbvs-slide-data]')).map((node) => ({
        index: Number(node.dataset.index || 0),
        title: node.dataset.title || '',
        number: node.dataset.number || '01',
        total: node.dataset.total || '01',
        videoText: node.dataset.videoText || '',
        ctaLabel: node.dataset.ctaLabel || '',
        ctaLink: node.dataset.ctaLink || '',
        descriptionHtml: node.innerHTML.trim()
      }));
      this.mq = window.matchMedia('(max-width: 989px)');
      this.scrollTrigger = null;
      this.activeVideoIndex = -1;
      this.lastProgress = null;
      this.scrollDirection = 1;
      this.retryTimer = null;
      this.initAttempt = 0;
      this.videoReadyHandlers = new WeakMap();
      this.resizeHandler = this.handleResize.bind(this);
      this.mediaHandler = this.handleResize.bind(this);
      this.loadHandler = this.handleLoad.bind(this);
      this.lastViewportWidth = window.innerWidth;
      this.metrics = null;
      this.stickFraction = 0;
      this.scrollRange = 0;
      this.clusterLift = 100;
      this.pinViewportHeight = window.innerHeight;
      this.pinHeightLocked = false;
      this.brandTopPx = NaN;
      this.brandHeight = 0;
      this.brandHeightIndex = -1;

      this.bindVideos();
      this.waitForSetup();

      window.addEventListener('resize', this.resizeHandler);
      window.addEventListener('load', this.loadHandler, { once: true });
      if (this.mq.addEventListener) {
        this.mq.addEventListener('change', this.mediaHandler);
      } else {
        this.mq.addListener(this.mediaHandler);
      }
    }

    waitForSetup() {
      clearTimeout(this.retryTimer);

      const canMeasure = this.track && this.stage && this.cluster && this.card && this.slideData.length > 0;

      if (window.gsap && window.ScrollTrigger && canMeasure) {
        this.initAttempt = 0;
        this.setup();
        return;
      }

      if (this.initAttempt > 20) {
        this.initAttempt = 0;
        this.setup();
        return;
      }

      this.initAttempt += 1;
      this.retryTimer = window.setTimeout(() => this.waitForSetup(), 150);
    }

    setup() {
      this.destroyScrollTrigger();
      this.updateCardTopOffset();
      this.measure();
      this.pinViewportHeight = window.innerHeight;
      this.pinHeightLocked = false;

      if (!window.gsap || !window.ScrollTrigger || !this.track || !this.stage || this.slideData.length === 0) {
        this.render(0);
        return;
      }

      window.gsap.registerPlugin(window.ScrollTrigger);
      const scrollTriggerConfig = {
        ignoreMobileResize: true
      };

      if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
        scrollTriggerConfig.autoRefreshEvents = 'visibilitychange,DOMContentLoaded,load';
      }

      window.ScrollTrigger.config(scrollTriggerConfig);
      this.scrollTrigger = window.ScrollTrigger.create({
        trigger: this.track,
        start: 'top top',
        end: 'bottom bottom',
        // Numeric scrub lerps the animation toward the scroll position instead
        // of snapping to raw scroll deltas, smoothing out choppy input.
        scrub: 0.6,
        invalidateOnRefresh: true,
        // Run from the moment the section enters so render() updates the card's
        // dvh position continuously (e.g. as the mobile URL bar settles).
        // Scaling itself is gated to start only once the cluster pins, via
        // stickFraction below, so the card scrolls with the page in normal
        // flow until then and there's no frozen-then-corrected jump at pin.
        onRefresh: (self) => {
          const range = self.end - self.start;
          this.scrollRange = range;
          this.stickFraction = range > 0 ? this.clamp(this.getStickStartOffset() / range) : 0;
        },
        onUpdate: (self) => this.render(self.progress)
      });

      this.render(0);
      window.ScrollTrigger.refresh();
    }

    getStickStartOffset() {
      if (!this.track || !this.cluster) return 0;

      // Absolute (layout) top of the track and the cluster. offsetTop walks the
      // static layout box and is unaffected by the cluster's sticky shift, so
      // this stays correct regardless of the current scroll position.
      const trackTop = this.track.getBoundingClientRect().top + window.scrollY;

      let clusterTop = 0;
      let node = this.cluster;
      while (node) {
        clusterTop += node.offsetTop;
        node = node.offsetParent;
      }

      const lift = this.getCssPx(
        window.getComputedStyle(this.section).getPropertyValue('--bbvs-cluster-lift'),
        100
      );

      // Scroll distance from "track top at viewport top" until the cluster
      // reaches its stuck position (top: -lift). Clamp so a bad measurement can
      // never eat more than the first slide's scroll budget.
      const offset = clusterTop - trackTop + lift;
      const max = this.track.offsetHeight * 0.4;
      return Math.max(0, Math.min(offset, max));
    }

    bindVideos() {
      this.layerVideos.forEach((vids, layerIndex) => {
        vids.forEach((video) => {
          if (!video) return;

          const readyHandler = () => {
            if (this.activeVideoIndex === layerIndex && this.activeVideo(layerIndex) === video) {
              this.playVideo(video);
            }
          };

          video.addEventListener('loadeddata', readyHandler);
          video.addEventListener('canplay', readyHandler);
          this.videoReadyHandlers.set(video, readyHandler);
        });
      });
    }

    activeVideo(layerIndex) {
      const vids = this.layerVideos[layerIndex];
      if (!vids || !vids.length) return null;
      if (vids.length === 1) return vids[0];

      // Split slide: pick the video whose breakpoint matches the viewport. The
      // mobile clip carries the --mobile modifier; everything else is desktop.
      const wantMobile = window.innerWidth < 990;
      const match = vids.find(
        (video) => video.classList.contains('brand-building-video-scroll__video--mobile') === wantMobile
      );
      return match || vids[0];
    }

    updateCardTopOffset() {
      if (!this.intro) return;

      const desktop = window.innerWidth >= 990;
      const gap = desktop ? 85 : 39;

      this.section.style.setProperty('--bbvs-card-top', `${gap}px`);
    }

    measure() {
      // Resolve everything that only changes on resize/layout once, so the
      // per-frame render() never has to call getComputedStyle (which forces a
      // style flush) while scrolling. This is the main fix for scroll jank.
      const desktop = window.innerWidth >= 990;
      const w = window.innerWidth;
      const styles = window.getComputedStyle(this.section);
      const cardWidthValue = styles.getPropertyValue('--bbvs-card-width').trim();
      const cardAspectRatioValue = styles.getPropertyValue('--bbvs-card-aspect-ratio').trim();
      const framedWidth = this.resolveCssLength(cardWidthValue, w, desktop ? Math.min(w * 0.48, 740) : w * 0.72);
      const framedHeight = framedWidth / this.parseAspectRatio(cardAspectRatioValue, desktop ? 16 / 9 : 1 / 0.98);

      this.metrics = {
        desktop,
        framedWidth,
        framedHeight,
        brandOffset: this.getCssPx(styles.getPropertyValue('--bbvs-brand-offset'), desktop ? 40 : 30),
        frameGapX: this.getCssPx(styles.getPropertyValue('--bbvs-frame-gap-x'), desktop ? 62 : 44),
        stickyTop: this.getCssPx(styles.getPropertyValue('--bbvs-sticky-top'), desktop ? 55 : 55),
        clusterMinHeight: this.getCssPx(styles.getPropertyValue('--bbvs-cluster-min-height'), desktop ? 65 : 65),
        clusterLift: this.getCssPx(styles.getPropertyValue('--bbvs-cluster-lift'), 100),
        contentLift: this.getCssPx(styles.getPropertyValue('--bbvs-content-lift'), 0)
      };
      this.clusterLift = this.metrics.clusterLift;
      // Force the brand height to be re-measured against the new layout.
      this.brandHeightIndex = -1;
      return this.metrics;
    }

    render(progress) {
      const slideCount = this.slideData.length;
      const totalSegments = Math.max(1, slideCount);
      // Gate scaling so it only begins once the cluster has pinned. Before the
      // pin (progress < stickFraction) the card stays framed and scrolls with
      // the page; after it, the remaining scroll is remapped to 0..1.
      const stickFraction = this.stickFraction || 0;
      const scalingProgress = stickFraction < 1 ? this.clamp((progress - stickFraction) / (1 - stickFraction)) : 0;
      const scaled = scalingProgress * totalSegments;
      const activeIndex = Math.min(slideCount - 1, Math.floor(Math.min(slideCount - 0.001, scaled)));
      const localProgress = scaled - activeIndex;
      const previousProgress = this.lastProgress;
      let scrollDirection = this.scrollDirection;

      if (previousProgress !== null && Math.abs(progress - previousProgress) > 0.0001) {
        scrollDirection = progress > previousProgress ? 1 : -1;
      }

      this.lastProgress = progress;
      this.scrollDirection = scrollDirection;

      const metrics = this.metrics || this.measure();
      const desktop = window.innerWidth >= 990;
      const w = window.innerWidth;
      const h = window.innerHeight;
      const brandOffset = metrics.brandOffset;
      const frameGapX = metrics.frameGapX;
      const stickyTop = metrics.stickyTop;
      const clusterMinHeight = metrics.clusterMinHeight;
      const clusterTop = 0;

      if (this.intro) {
        const introOut = this.ramp(progress, 0.08, 0.2);
        this.intro.style.opacity = `${1 - introOut}`;
        this.intro.style.transform = `translateY(${-30 * introOut}px)`;
      }

      const framedWidth = metrics.framedWidth;
      const framedHeight = metrics.framedHeight;
      const fullHeight = Math.max(0, h - stickyTop);
      const framed = {
        x: (w - framedWidth) / 2,
        y: Math.max(0, (fullHeight - framedHeight) / 2),
        w: framedWidth,
        h: framedHeight
      };
      const full = { x: 0, y: 0, w, h: fullHeight };

      let geometry = framed;

      // Scale on a gentle smootherstep curve that begins as soon as the slide
      // is entered (0.06) so the whole scale-up is gradual and spread out,
      // rather than snapping through the mid-scale on a steeper ease.
      if (localProgress < 0.06) {
        geometry = framed;
      } else if (localProgress < 0.5) {
        const t = this.smoother(this.ramp(localProgress, 0.06, 0.5));
        geometry = this.blend(framed, full, t);
      } else if (localProgress < 0.94) {
        const t = this.smoother(this.ramp(localProgress, 0.5, 0.94));
        geometry = this.blend(full, framed, t);
      } else {
        geometry = framed;
      }

      // The card is absolutely positioned inside the sticky cluster, so it
      // already scrolls up with the page in lockstep before the pin and is
      // exactly centered the moment the cluster sticks. We intentionally do NOT
      // add a JS-modelled "ease into center" offset during the approach: that
      // offset is derived from the scroll model (scrollRange/stickFraction),
      // which disagrees with the browser's real CSS-sticky position by a few px
      // right at the handoff. With scrub smoothing on, that few-px disagreement
      // is played out as a ~1s up/down bounce of the card and brand exactly when
      // the cluster pins. Tracking the real cluster position keeps the takeover
      // seamless. rawPreStickDistance is still computed below because the mobile
      // viewport-height lock and the recenter fade depend on it.
      const rawPreStickDistance = Math.max(0, (stickFraction - progress) * (this.scrollRange || 0));
      const pinSettleDistance = desktop ? 6 : 12;
      const preStickDistance = Math.max(0, rawPreStickDistance - pinSettleDistance);

      if (!desktop) {
        if (rawPreStickDistance > pinSettleDistance * 2) {
          this.pinHeightLocked = false;
          this.pinViewportHeight = h;
        } else if (!this.pinHeightLocked) {
          this.pinViewportHeight = h;
          this.pinHeightLocked = true;
        }
      }

      const entryDistance = Math.min(240, h * 0.25);
      const entryProgress = entryDistance > 0 ?
        this.smoother(1 - this.clamp(preStickDistance / entryDistance)) :
        1;
      const fullness = this.clamp((geometry.h - framed.h) / ((full.h - framed.h) || 1));
      // Recenter more slowly than the card scales. Squaring fullness means ~30%
      // scale progress yields only ~10% of the Y-axis correction, so the card
      // (lifted up by --bbvs-content-lift in the framed state) eases toward
      // center Y without a visible layout shift. Applies on desktop and mobile.
      const centerProgress = fullness * fullness;
      const centerViewportHeight = desktop ? h : this.mix(this.pinViewportHeight, h, centerProgress);
      const centeredY = Math.max(0, (centerViewportHeight - stickyTop - geometry.h) / 2);
      const contentLift = metrics.contentLift * (1 - centerProgress);
      const recenterFade = desktop ? 1 : 1 - Math.sin(entryProgress * Math.PI) * 0.22;

      geometry = { ...geometry, y: Math.max(0, centeredY - contentLift) };

      const handlesOpacity = 1 - this.ramp(fullness, 0.08, 0.42);
      let previewIndex = null;
      let handoffDirection = 1;
      let handoffProgress = 0;

      if (desktop && scrollDirection < 0 && activeIndex > 0) {
        previewIndex = activeIndex - 1;
        handoffDirection = -1;
        handoffProgress = this.ease(1 - this.ramp(localProgress, 0.02, 0.28));
      } else if (desktop && scrollDirection >= 0 && activeIndex < slideCount - 1) {
        previewIndex = activeIndex + 1;
        handoffDirection = 1;
        handoffProgress = this.ease(this.ramp(localProgress, 0.76, 0.98));
      }

      this.updateClusterHeight(fullness, clusterMinHeight);
      this.setCardGeometry(geometry, frameGapX, fullness, handoffProgress, handoffDirection, recenterFade);
      // Build the next card from the SAME geometry as the main card (not raw
      // `framed`) so the two share size and the lifted position. Otherwise the
      // incoming card sits lower (missing the content-lift) and smaller (missing
      // the live shrink scale) than the outgoing card, which breaks the swap.
      this.setNextCardGeometry(geometry, fullness, desktop, previewIndex, handoffProgress, handoffDirection);

      if (this.handles) {
        this.handles.style.opacity = `${handlesOpacity}`;
      }

      if (this.expand) {
        this.expand.classList.toggle('is-hidden', fullness > 0.12);
      }

      const currentData = this.slideData[activeIndex];
      const previousData = this.slideData[Math.max(activeIndex - 1, 0)];
      const nextData = this.slideData[Math.min(activeIndex + 1, slideCount - 1)];

      this.updateMedia(activeIndex);
      this.updateNextMedia(previewIndex);
      this.updateOwned(currentData, desktop, geometry, fullness);
      this.updateBrand(
        currentData,
        previousData,
        nextData,
        geometry,
        desktop,
        h,
        brandOffset,
        clusterTop,
        fullness,
        activeIndex,
        localProgress,
        recenterFade
      );
      // The brand block's height only changes when the slide changes, so only
      // pay the offsetHeight layout read on those frames instead of every one.
      if (this.brand && activeIndex !== this.brandHeightIndex) {
        this.brandHeightIndex = activeIndex;
        this.brandHeight = this.brand.offsetHeight || 0;
      }
      this.expandClusterToFitBrand(fullness, clusterMinHeight);
      this.syncVideos(activeIndex);
    }

    setCardGeometry(geometry, frameGapX, fullness, handoffProgress, handoffDirection, recenterFade = 1) {
      if (!this.card || !this.handles) return;

      const viewportWidth = window.innerWidth || 1;
      const viewportHeight = window.innerHeight || 1;
      const cardGeometry = this.clampGeometryToViewport(geometry, viewportWidth, viewportHeight);
      const cardTopOffset = this.getRecenterOffset(fullness, viewportHeight);
      const cardTop = cardGeometry.y - cardTopOffset;

      this.card.style.left = this.toViewportWidth(cardGeometry.x, viewportWidth);
      this.card.style.top = this.toViewportHeight(cardTop, viewportHeight);
      this.card.style.width = this.toViewportWidth(cardGeometry.w, viewportWidth);
      this.card.style.height = this.toViewportHeight(cardGeometry.h, viewportHeight);
      this.card.style.zIndex = handoffProgress > 0.62 ? '8' : '10';
      this.card.style.opacity = `${recenterFade * (1 - handoffProgress * 0.18)}`;
      this.card.style.transform =
        `translateY(${this.mix(0, -18 * handoffDirection, handoffProgress)}px) ` +
        `scale(${this.mix(1, 0.92, handoffProgress)}) ` +
        `rotateX(${this.mix(0, 7 * handoffDirection, handoffProgress)}deg)`;

      this.handles.style.left = this.toViewportWidth(cardGeometry.x - frameGapX, viewportWidth);
      this.handles.style.top = this.toViewportHeight(cardTop, viewportHeight);
      this.handles.style.width = this.toViewportWidth(cardGeometry.w + frameGapX * 2, viewportWidth);
      this.handles.style.height = this.toViewportHeight(cardGeometry.h, viewportHeight);
      this.handles.style.transform = this.card.style.transform;
      // The handles frame the main card and inherit its transform, so when the
      // main card recedes to the back during a swap (z 8, handoffProgress > 0.62)
      // the frame must drop below the incoming front card (z 12) too. Otherwise
      // its fixed z-index keeps the full-width h-top/h-bottom lines painted over
      // the front video card. 9 keeps the frame just above the receded card it
      // belongs to, but behind the front card.
      this.handles.style.zIndex = handoffProgress > 0.62 ? '9' : '14';
      this.handles.style.visibility = 'visible';
    }

    updateClusterHeight(fullness, minHeight) {
      if (!this.cluster) return;

      const clusterHeight = this.mix(minHeight, 100, fullness);
      this.cluster.style.setProperty('--bbvs-cluster-height', `${clusterHeight}dvh`);
    }

    expandClusterToFitBrand(fullness, minHeight) {
      if (!this.cluster || !this.brand) return;

      const viewportHeight = window.innerHeight || 1;
      const baseHeight = this.mix(minHeight, 100, fullness);
      const brandTop = this.brandTopPx;
      const brandHeight = this.brandHeight || 0;

      if (!Number.isFinite(brandTop) || !brandHeight) return;

      const requiredHeight = ((brandTop + brandHeight + 92) / viewportHeight) * 100;
      const clusterHeight = Math.min(112, Math.max(baseHeight, requiredHeight));

      this.cluster.style.setProperty('--bbvs-cluster-height', `${clusterHeight}dvh`);
    }

    setNextCardGeometry(geometry, fullness, desktop, previewIndex, handoffProgress, handoffDirection) {
      if (!this.nextCard) return;

      if (!desktop) {
        this.nextCard.style.opacity = '0';
        this.nextCard.style.visibility = 'hidden';
        return;
      }

      const hasPreviewCard = previewIndex !== null;
      const stackOpacity = hasPreviewCard && handoffDirection > 0 ? 1 - this.ramp(fullness, 0.02, 0.24) : 0;
      const viewportWidth = window.innerWidth || 1;
      const viewportHeight = window.innerHeight || 1;
      const scale = this.mix(0.84, 1, handoffProgress);
      const stackTopOffset = this.getRecenterOffset(fullness, viewportHeight);
      const previewWidth = geometry.w * scale;
      const previewHeight = geometry.h * scale;
      const previewX = geometry.x + (geometry.w - previewWidth) / 2;
      const previewY = this.mix(
        geometry.y - 24 * handoffDirection - stackTopOffset,
        geometry.y - stackTopOffset,
        handoffProgress
      );
      const previewGeometry = this.clampGeometrySizeToViewport(
        { x: previewX, y: previewY, w: previewWidth, h: previewHeight },
        viewportWidth,
        viewportHeight
      );

      this.nextCard.style.left = this.toViewportWidth(previewGeometry.x, viewportWidth);
      this.nextCard.style.top = this.toViewportHeight(previewGeometry.y, viewportHeight);
      this.nextCard.style.width = this.toViewportWidth(previewGeometry.w, viewportWidth);
      this.nextCard.style.height = this.toViewportHeight(previewGeometry.h, viewportHeight);
      this.nextCard.style.opacity = `${Math.max(stackOpacity, handoffProgress)}`;
      this.nextCard.style.visibility = stackOpacity > 0.01 || handoffProgress > 0.01 ? 'visible' : 'hidden';
      this.nextCard.style.zIndex = handoffProgress > 0.62 ? '12' : '8';
      this.nextCard.style.transform =
        `rotateX(${this.mix(-8 * handoffDirection, 0, handoffProgress)}deg) ` +
        `translateY(${this.mix(12 * handoffDirection, 0, handoffProgress)}px)`;
    }

    updateMedia(activeIndex) {
      this.mediaLayers.forEach((layer, index) => {
        const opacity = index === activeIndex ? 1 : 0;
        layer.style.opacity = `${opacity}`;
        layer.classList.toggle('is-active', opacity > 0.5);
      });
    }

    updateNextMedia(previewIndex) {
      this.nextMediaLayers.forEach((layer, index) => {
        const opacity = index === previewIndex ? 1 : 0;
        layer.style.opacity = `${opacity}`;
        layer.classList.toggle('is-active', opacity > 0.5);
      });
    }

    updateOwned(data, desktop, geometry, fullness) {
      if (!this.owned) return;

      this.owned.textContent = data.videoText;

      if (desktop) {
        this.owned.style.left = '34px';
        this.owned.style.right = 'auto';
        this.owned.style.top = '20px';
        this.owned.style.textAlign = 'left';
      } else {
        this.owned.style.left = '0';
        this.owned.style.right = '0';
        this.owned.style.top = `${this.mix(geometry.h - 46, 80, fullness)}px`;
        this.owned.style.textAlign = 'center';
      }

      this.owned.style.opacity = `${fullness > 0.7 ? 1 : 0.85}`;
    }

    updateBrand(
      currentData,
      previousData,
      nextData,
      geometry,
      desktop,
      viewportHeight,
      brandOffset,
      clusterTop,
      fullness,
      activeIndex,
      localProgress,
      recenterFade = 1
    ) {
      if (!this.brand || !this.brandName || !this.brandNum || !this.brandGhost || !this.brandDesc || !this.brandBtn || !this.brandTick) {
        return;
      }

      const framedBrand = 1 - this.ramp(fullness, 0.16, 0.5);
      const recenterOffset = this.getRecenterOffset(fullness, viewportHeight);
      const brandTopLift = 70 * this.ease(this.ramp(fullness, 0.12, 0.5));
      const ghostData = nextData;
      const mobileSlideTextFade = desktop ?
        1 :
        activeIndex === 0 ?
          1 - this.ramp(localProgress, 0.42, 0.5) :
          this.ramp(localProgress, 0.02, 0.24);

      this.brandName.textContent = currentData.title;
      this.brandNum.textContent = `${currentData.number} / ${currentData.total}`;
      this.brandGhost.textContent = ghostData && ghostData !== currentData ? ghostData.title : '';
      this.brandDesc.innerHTML = currentData.descriptionHtml;

      if (currentData.ctaLabel && currentData.ctaLink) {
        this.brandBtn.textContent = currentData.ctaLabel;
        this.brandBtn.href = currentData.ctaLink;
        this.brandBtn.style.display = '';
      } else {
        this.brandBtn.removeAttribute('href');
        this.brandBtn.textContent = '';
        this.brandBtn.style.display = 'none';
      }

      this.brand.classList.remove('is-overlay');
      this.brand.style.color = '#0d0d0d';

      if (desktop) {
        this.brand.classList.remove('is-mobile-above-card');
        this.brand.style.opacity = `${framedBrand}`;
        this.brandTopPx = geometry.y - recenterOffset + geometry.h + brandOffset - brandTopLift;
        // Use dvh (same live unit as the card) so the brand tracks the card
        // when the mobile URL bar shows/hides, instead of freezing in px and
        // letting the card overlap it before the cluster sticks.
        this.brand.style.top = this.toViewportHeight(this.brandTopPx, viewportHeight);
        this.brandNum.style.display = 'none';
        this.brandTick.style.display = 'none';
        this.brandGhost.style.display = 'none';
        this.brandDesc.style.display = '';
        this.brandBtn.style.display = currentData.ctaLabel && currentData.ctaLink ? '' : 'none';
      } else {
        const mobileBelowTop = geometry.y - recenterOffset + geometry.h + brandOffset - brandTopLift;
        const mobileFade = 1;
        const isLastSlide = activeIndex >= this.slideData.length - 1;

        this.brand.classList.remove('is-mobile-above-card');
        this.brandNum.style.display = '';
        this.brandTick.style.display = isLastSlide ? 'none' : 'block';
        this.brandGhost.style.display = ghostData && ghostData !== currentData ? '' : 'none';
        this.brandDesc.style.display = '';
        this.brandBtn.style.display = currentData.ctaLabel && currentData.ctaLink ? '' : 'none';

        this.brand.style.opacity = `${framedBrand * mobileFade * mobileSlideTextFade * recenterFade}`;
        this.brandTopPx = mobileBelowTop;
        // dvh keeps the brand locked to the card (which is also in dvh) as the
        // mobile viewport changes, so it stays below the card by default
        // instead of overlapping before the cluster sticks.
        this.brand.style.top = this.toViewportHeight(mobileBelowTop, viewportHeight);
      }

      this.brandGhost.style.fontWeight = '300';
      this.brandGhost.style.letterSpacing = '0.14em';
    }

    syncVideos(activeIndex) {
      const indexChanged = activeIndex !== this.activeVideoIndex;
      this.activeVideoIndex = activeIndex;

      this.layerVideos.forEach((vids, layerIndex) => {
        const active = layerIndex === activeIndex ? this.activeVideo(layerIndex) : null;

        vids.forEach((video) => {
          if (!video) return;

          if (video === active) {
            video.muted = true;
            video.loop = true;
            video.playsInline = true;
            if (indexChanged || video.paused) {
              this.playVideo(video);
            }
          } else {
            // Also pauses the off-breakpoint clip in the active layer.
            video.pause();
          }
        });
      });
    }

    playVideo(video) {
      video.play().catch(() => {});
    }

    ramp(value, start, end) {
      return this.clamp((value - start) / (end - start));
    }

    blend(from, to, progress) {
      return {
        x: this.mix(from.x, to.x, progress),
        y: this.mix(from.y, to.y, progress),
        w: this.mix(from.w, to.w, progress),
        h: this.mix(from.h, to.h, progress)
      };
    }

    mix(start, end, progress) {
      return start + (end - start) * progress;
    }

    getCssPx(value, fallback) {
      const numericValue = parseFloat(value);
      return Number.isFinite(numericValue) ? numericValue : fallback;
    }

    getRecenterOffset(fullness, viewportHeight) {
      // The cluster is sticky at top: calc(sticky-top - cluster-lift), so when
      // stuck its top edge sits `cluster-lift` px above the viewport. We add
      // that lift back as a CONSTANT (not ramped by fullness) so the card's
      // center stays pinned to the true viewport center at every scale step.
      // A fullness-ramped offset would slide the center downward as the card
      // grows, which reads as the card bobbing up/down during early scaling.
      // Constant compensation keeps it fixed in place while it grows, and the
      // full state still lands flush at the bottom (no blank band). Returned
      // negative because the callers subtract this offset from the card's top.
      const lift = Number.isFinite(this.clusterLift) ? this.clusterLift : 100;
      return -lift;
    }

    clampGeometryToViewport(geometry, viewportWidth, viewportHeight) {
      const width = Math.min(geometry.w, viewportWidth);
      const height = Math.min(geometry.h, viewportHeight);
      const x = Math.min(Math.max(geometry.x, 0), Math.max(0, viewportWidth - width));
      const y = Math.min(Math.max(geometry.y, 0), Math.max(0, viewportHeight - height));

      return { x, y, w: width, h: height };
    }

    clampGeometrySizeToViewport(geometry, viewportWidth, viewportHeight) {
      return {
        x: geometry.x,
        y: geometry.y,
        w: Math.min(geometry.w, viewportWidth),
        h: Math.min(geometry.h, viewportHeight)
      };
    }

    toViewportWidth(value, viewportWidth) {
      return `${(value / viewportWidth) * 100}vw`;
    }

    toViewportHeight(value, viewportHeight) {
      return `${(value / viewportHeight) * 100}dvh`;
    }

    resolveCssLength(value, viewportWidth, fallback) {
      if (!value) return fallback;

      if (value.startsWith('min(') || value.startsWith('max(') || value.startsWith('clamp(')) {
        const temp = document.createElement('div');
        temp.style.position = 'absolute';
        temp.style.visibility = 'hidden';
        temp.style.width = value;
        temp.style.pointerEvents = 'none';
        document.body.appendChild(temp);
        const width = temp.getBoundingClientRect().width;
        temp.remove();
        return width || fallback;
      }

      if (value.endsWith('vw')) {
        return (parseFloat(value) / 100) * viewportWidth;
      }

      if (value.endsWith('px')) {
        return parseFloat(value);
      }

      return fallback;
    }

    parseAspectRatio(value, fallback) {
      if (!value || !value.includes('/')) return fallback;
      const parts = value.split('/').map((part) => parseFloat(part.trim()));
      if (parts.length !== 2 || !parts[0] || !parts[1]) return fallback;
      return parts[0] / parts[1];
    }

    clamp(value) {
      return Math.max(0, Math.min(1, value));
    }

    ease(value) {
      const t = this.clamp(value);
      return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    }

    smoother(value) {
      const t = this.clamp(value);
      return t * t * t * (t * (t * 6 - 15) + 10);
    }

    handleResize() {
      const viewportWidth = window.innerWidth;
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

      if (isTouchDevice && viewportWidth === this.lastViewportWidth) {
        return;
      }

      this.lastViewportWidth = viewportWidth;
      this.waitForSetup();
    }

    handleLoad() {
      this.waitForSetup();
      if (window.ScrollTrigger) {
        window.ScrollTrigger.refresh();
      }
    }

    destroyScrollTrigger() {
      clearTimeout(this.retryTimer);

      if (this.scrollTrigger) {
        this.scrollTrigger.kill();
        this.scrollTrigger = null;
      }

      this.videos.forEach((video) => {
        if (video) video.pause();
      });
    }

    destroy() {
      this.destroyScrollTrigger();
      this.videos.forEach((video) => {
        const readyHandler = this.videoReadyHandlers.get(video);
        if (video && readyHandler) {
          video.removeEventListener('loadeddata', readyHandler);
          video.removeEventListener('canplay', readyHandler);
        }
      });
      window.removeEventListener('resize', this.resizeHandler);
      window.removeEventListener('load', this.loadHandler);
      if (this.mq.removeEventListener) {
        this.mq.removeEventListener('change', this.mediaHandler);
      } else {
        this.mq.removeListener(this.mediaHandler);
      }
    }
  }

  const instances = new Map();

  const mount = () => {
    document.querySelectorAll('[data-brand-building-video-scroll]').forEach((section) => {
      if (!instances.has(section)) {
        instances.set(section, new BrandBuildingVideoScroll(section));
      }
    });
  };

  document.addEventListener('shopify:section:unload', (event) => {
    const section =
      event.target.matches?.('[data-brand-building-video-scroll]') ?
        event.target :
        event.target.querySelector('[data-brand-building-video-scroll]');

    if (!section) return;

    const instance = instances.get(section);
    if (instance) {
      instance.destroy();
      instances.delete(section);
    }
  });

  document.addEventListener('shopify:section:load', mount);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount);
  } else {
    mount();
  }
})();
